/**
 * Plan Modal Component
 * Reusable modal for creating and editing plans
 * Task 002 - Plans Management
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  initialName?: string;
  mode: 'create' | 'edit';
}

export const PlanModal: React.FC<PlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialName = '',
  mode,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setError('');
    }
  }, [isOpen, initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Nama plan harus diisi');
      return;
    }

    if (name.trim().length < 3) {
      setError('Nama plan minimal 3 karakter');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(name.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '8px 8px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {mode === 'create' ? 'Tambah Plan' : 'Edit Plan'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              Nama Plan
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder="Budget Bulanan 2024"
              disabled={isSubmitting}
              autoFocus
            />
            {error && (
              <div
                className="mt-3 bg-red-500 border-2 border-red-700 px-3 py-2"
                style={{ boxShadow: '3px 3px 0 #c0392b' }}
              >
                <p className="font-mono text-xs text-white">{error}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '4px 4px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase disabled:opacity-50"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {isSubmitting ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
